import { Graphics, Text } from 'pixi.js';
import { GATEWAY_CONFIG } from '@astra/shared';
import type { EntityType, SelectionInfo } from '@astra/shared';
import { TargetableEntity } from './base/TargetableEntity';

// Gateway color theme - cyan/teal energy portal
const GATEWAY_COLOR = 0x00dddd;      // Cyan primary
const GATEWAY_ACCENT = 0x00ffff;     // Bright cyan accent
const GATEWAY_DIM = 0x006666;        // Dimmed cyan when far
const GATEWAY_COOLDOWN = 0xff4444;   // Red when on cooldown

export class Gateway extends TargetableEntity {
  // SpaceEntity abstract properties
  public readonly id: string;
  public readonly entityType: EntityType = 'gateway';

  // TargetableEntity abstract properties
  public readonly selectionRadius: number = GATEWAY_CONFIG.VISUAL_RADIUS;
  public readonly isHostile: boolean = false;

  // Gateway-specific properties
  public readonly targetConstellationName: string;
  public readonly targetConstellationX: number;
  public readonly targetConstellationY: number;
  public readonly travelCost: number;

  private outerRing: Graphics;
  private innerRing: Graphics;
  private energyField: Graphics;
  private coreGraphics: Graphics;
  private nameText: Text;

  private rotationSpeed: number = 0.001;
  private pulseTime: number = 0;
  private ringRotation: number = 0;
  private _isInRange: boolean = false;
  private _isOnCooldown: boolean = false;

  constructor(
    x: number,
    y: number,
    gatewayId: string,
    targetConstellationName: string,
    targetConstellationX: number,
    targetConstellationY: number,
    travelCost: number
  ) {
    super();

    this.position.set(x, y);
    this.id = gatewayId;
    this.targetConstellationName = targetConstellationName;
    this.targetConstellationX = targetConstellationX;
    this.targetConstellationY = targetConstellationY;
    this.travelCost = travelCost;

    // Enable sorting by zIndex
    this.sortableChildren = true;

    // Create graphics layers
    this.outerRing = new Graphics();
    this.outerRing.zIndex = 0;
    this.addChild(this.outerRing);

    this.innerRing = new Graphics();
    this.innerRing.zIndex = 1;
    this.addChild(this.innerRing);

    this.energyField = new Graphics();
    this.energyField.zIndex = 2;
    this.addChild(this.energyField);

    this.coreGraphics = new Graphics();
    this.coreGraphics.zIndex = 3;
    this.addChild(this.coreGraphics);

    // Draw static outer ring
    this.drawOuterRing();

    // Create name label showing destination
    this.nameText = new Text({
      text: `→ ${this.targetConstellationName}`,
      style: {
        fontFamily: 'monospace',
        fontSize: 12,
        fill: GATEWAY_COLOR,
        stroke: { color: 0x0a1628, width: 2 },
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -GATEWAY_CONFIG.VISUAL_RADIUS - 25);
    this.nameText.zIndex = 4;
    this.addChild(this.nameText);
  }

  // TargetableEntity abstract method implementation
  getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.id,
      name: this.targetConstellationName,
      x: this.x,
      y: this.y,
      data: {
        targetConstellationX: this.targetConstellationX,
        targetConstellationY: this.targetConstellationY,
        travelCost: this.travelCost,
        isInRange: this._isInRange,
        isOnCooldown: this._isOnCooldown,
      },
    };
  }

  private drawOuterRing(): void {
    const radius = GATEWAY_CONFIG.VISUAL_RADIUS;
    const color = this._isOnCooldown ? GATEWAY_COOLDOWN : (this._isInRange ? GATEWAY_ACCENT : GATEWAY_DIM);
    const alpha = this._isInRange ? 0.8 : 0.4;

    this.outerRing.clear();

    // Dashed outer circle
    const dashCount = 16;
    const dashArc = (Math.PI * 2) / dashCount * 0.6;

    for (let i = 0; i < dashCount; i++) {
      const startAngle = (Math.PI * 2 / dashCount) * i + this.ringRotation;
      this.outerRing.arc(0, 0, radius, startAngle, startAngle + dashArc);
      this.outerRing.stroke({ width: 3, color, alpha });
    }

    // Outer marker triangles pointing inward
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI / 2) * i + this.ringRotation;
      const mx = Math.cos(angle) * (radius + 15);
      const my = Math.sin(angle) * (radius + 15);

      const tipX = Math.cos(angle) * (radius + 5);
      const tipY = Math.sin(angle) * (radius + 5);

      const perpAngle = angle + Math.PI / 2;
      const base1X = mx + Math.cos(perpAngle) * 8;
      const base1Y = my + Math.sin(perpAngle) * 8;
      const base2X = mx - Math.cos(perpAngle) * 8;
      const base2Y = my - Math.sin(perpAngle) * 8;

      this.outerRing.poly([tipX, tipY, base1X, base1Y, base2X, base2Y], true);
      this.outerRing.fill({ color, alpha: alpha * 0.8 });
    }
  }

  private drawInnerRing(): void {
    const radius = GATEWAY_CONFIG.VISUAL_RADIUS * 0.7;
    const color = this._isOnCooldown ? GATEWAY_COOLDOWN : GATEWAY_COLOR;
    const alpha = this._isInRange ? 0.6 : 0.3;

    this.innerRing.clear();

    // Inner rotating ring - opposite direction
    const dashCount = 12;
    const dashArc = (Math.PI * 2) / dashCount * 0.5;

    for (let i = 0; i < dashCount; i++) {
      const startAngle = (Math.PI * 2 / dashCount) * i - this.ringRotation * 1.5;
      this.innerRing.arc(0, 0, radius, startAngle, startAngle + dashArc);
      this.innerRing.stroke({ width: 2, color, alpha });
    }
  }

  private drawEnergyField(): void {
    this.energyField.clear();

    const color = this._isOnCooldown ? GATEWAY_COOLDOWN : GATEWAY_ACCENT;
    const baseAlpha = this._isInRange ? 0.15 : 0.05;

    // Swirling energy arcs
    const arcCount = 6;
    for (let i = 0; i < arcCount; i++) {
      const baseAngle = (Math.PI * 2 / arcCount) * i + this.pulseTime * 0.5;
      const pulse = Math.sin(this.pulseTime * 3 + i) * 0.5 + 0.5;
      const arcRadius = GATEWAY_CONFIG.VISUAL_RADIUS * (0.3 + pulse * 0.35);

      this.energyField.arc(0, 0, arcRadius, baseAngle, baseAngle + Math.PI / 4);
      this.energyField.stroke({ width: 2, color, alpha: baseAlpha + pulse * 0.1 });
    }

    // Central energy spiral
    const spiralPoints: number[] = [];
    for (let t = 0; t < Math.PI * 4; t += 0.2) {
      const r = 20 + t * 8;
      if (r > GATEWAY_CONFIG.VISUAL_RADIUS * 0.6) break;
      const angle = t + this.pulseTime;
      spiralPoints.push(Math.cos(angle) * r, Math.sin(angle) * r);
    }
    if (spiralPoints.length >= 4) {
      this.energyField.moveTo(spiralPoints[0], spiralPoints[1]);
      for (let i = 2; i < spiralPoints.length; i += 2) {
        this.energyField.lineTo(spiralPoints[i], spiralPoints[i + 1]);
      }
      this.energyField.stroke({ width: 1, color, alpha: baseAlpha * 2 });
    }
  }

  private drawCore(): void {
    this.coreGraphics.clear();

    const pulse = Math.sin(this.pulseTime * 2) * 0.3 + 0.7;
    const color = this._isOnCooldown ? GATEWAY_COOLDOWN : GATEWAY_ACCENT;
    const alpha = this._isInRange ? 0.4 * pulse : 0.2 * pulse;

    // Pulsing core
    this.coreGraphics.circle(0, 0, 25 + pulse * 10);
    this.coreGraphics.fill({ color, alpha });

    // Core ring
    this.coreGraphics.circle(0, 0, 35);
    this.coreGraphics.stroke({ width: 2, color, alpha: alpha * 1.5 });

    // Bright center point
    this.coreGraphics.circle(0, 0, 8);
    this.coreGraphics.fill({ color: 0xffffff, alpha: alpha * 0.8 });
  }

  setInRange(inRange: boolean): void {
    if (this._isInRange !== inRange) {
      this._isInRange = inRange;
      this.drawOuterRing();
      // Update name text color
      this.nameText.style.fill = this._isOnCooldown ? GATEWAY_COOLDOWN : (inRange ? GATEWAY_ACCENT : GATEWAY_COLOR);
    }
  }

  setCooldown(onCooldown: boolean): void {
    if (this._isOnCooldown !== onCooldown) {
      this._isOnCooldown = onCooldown;
      this.drawOuterRing();
      this.nameText.style.fill = onCooldown ? GATEWAY_COOLDOWN : (this._isInRange ? GATEWAY_ACCENT : GATEWAY_COLOR);
    }
  }

  get isInRange(): boolean {
    return this._isInRange;
  }

  get isOnCooldown(): boolean {
    return this._isOnCooldown;
  }

  // Check if a point is within activation range
  isInActivationRange(x: number, y: number): boolean {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < GATEWAY_CONFIG.ACTIVATION_RADIUS;
  }

  // Get distance to gateway center
  getDistanceTo(x: number, y: number): number {
    const dx = x - this.x;
    const dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  update(delta: number): void {
    // Update animation times
    this.pulseTime += delta / 1000;
    this.ringRotation += this.rotationSpeed * delta;

    // Redraw animated elements
    this.drawOuterRing();
    this.drawInnerRing();
    this.drawEnergyField();
    this.drawCore();
  }

  destroy(): void {
    this.outerRing.destroy();
    this.innerRing.destroy();
    this.energyField.destroy();
    this.coreGraphics.destroy();
    this.nameText.destroy();
    super.destroy();
  }

  // Legacy getter for backward compatibility
  public get gatewayId(): string {
    return this.id;
  }

  // Get position for radar display
  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }
}
